import React from 'react';

/**
 * GamificationSettings - Panel for configuring quiz gamification options
 * 
 * @param {Object} props
 * @param {Object} props.settings - Current settings object
 * @param {Function} props.onUpdateSettings - Callback to update settings (receives partial settings object)
 */
function GamificationSettings({ settings = {}, onUpdateSettings }) {
  const gamificationEnabled = settings.enableGamification ?? true;

  return (
    <div className="space-y-3">
      <label className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <span className="text-sm text-gray-700">Punkte & Rangliste aktivieren</span>
        <input
          type="checkbox"
          checked={gamificationEnabled}
          onChange={(e) => onUpdateSettings({ enableGamification: e.target.checked })}
          className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
        />
      </label>
      
      {gamificationEnabled && (
        <div className="space-y-2 pl-2 border-l-2 border-blue-200">
          <label className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
            <span className="text-sm text-gray-700">⏱️ Zeitbonus</span>
            <input
              type="checkbox"
              checked={settings.enableTimeBonus ?? true}
              onChange={(e) => onUpdateSettings({ enableTimeBonus: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
          
          <label className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
            <span className="text-sm text-gray-700">🏆 Rangliste anzeigen</span>
            <input
              type="checkbox"
              checked={settings.showLeaderboard ?? true}
              onChange={(e) => onUpdateSettings({ showLeaderboard: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
          
          <label className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
            <span className="text-sm text-gray-700">🔥 Streak-Zähler anzeigen</span>
            <input
              type="checkbox"
              checked={settings.showStreak ?? true}
              onChange={(e) => onUpdateSettings({ showStreak: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
          
          <label className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
            <span className="text-sm text-gray-700">🔒 Antworten sperren bei Auflösung</span>
            <input
              type="checkbox"
              checked={settings.lockOnReveal ?? true}
              onChange={(e) => onUpdateSettings({ lockOnReveal: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
          
          <label className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
            <span className="text-sm text-gray-700">⏱️ Zeitlimit pro Frage</span>
            <input
              type="checkbox"
              checked={settings.enableQuestionTimer ?? false}
              onChange={(e) => onUpdateSettings({ enableQuestionTimer: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
          
          {settings.enableQuestionTimer && (
            <div className="flex items-center justify-between p-2 bg-orange-50 rounded-lg ml-4">
              <span className="text-sm text-gray-700">⏰ Sekunden pro Frage</span>
              <input
                type="number"
                value={settings.questionTimerSeconds ?? 30}
                onChange={(e) => onUpdateSettings({ questionTimerSeconds: parseInt(e.target.value) || 30 })}
                className="w-20 px-2 py-1 border border-gray-200 rounded text-sm text-center"
                min="5"
                max="300"
              />
            </div>
          )}
          
          <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
            <span className="text-sm text-gray-700">💯 Basispunkte pro Frage</span>
            <input
              type="number"
              value={settings.basePointsPerQuestion ?? 100}
              onChange={(e) => onUpdateSettings({ basePointsPerQuestion: parseInt(e.target.value) || 100 })}
              className="w-20 px-2 py-1 border border-gray-200 rounded text-sm text-center"
              min="1"
            />
          </div>
          
          <label className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
            <span className="text-sm text-gray-700">🔥 Streak-Bonus aktivieren</span>
            <input
              type="checkbox"
              checked={settings.enableStreakBonus ?? false}
              onChange={(e) => onUpdateSettings({ enableStreakBonus: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
          
          {settings.enableStreakBonus && (
            <div className="flex items-center justify-between p-2 bg-orange-50 rounded-lg ml-4">
              <span className="text-sm text-gray-700">🔥 Bonus-Punkte pro Streak</span>
              <input
                type="number"
                value={settings.streakBonusPoints ?? 50}
                onChange={(e) => onUpdateSettings({ streakBonusPoints: parseInt(e.target.value) || 50 })}
                className="w-20 px-2 py-1 border border-gray-200 rounded text-sm text-center"
                min="0"
                step="10"
              />
            </div>
          )}
          
          <label className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
            <span className="text-sm text-gray-700">🃏 Joker aktivieren</span>
            <input
              type="checkbox"
              checked={settings.enableJokers ?? false}
              onChange={(e) => onUpdateSettings({ enableJokers: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
          
          {settings.enableJokers && (
            <div className="space-y-2 pl-4 border-l-2 border-purple-200">
              <label className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                <span className="text-sm text-gray-700">🎯 50:50 Joker</span>
                <input
                  type="checkbox"
                  checked={settings.enableJoker5050 ?? true}
                  onChange={(e) => onUpdateSettings({ enableJoker5050: e.target.checked })}
                  className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                />
              </label>
              
              <label className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                <span className="text-sm text-gray-700">📊 Publikumsjoker (Ergebnisse)</span>
                <input
                  type="checkbox"
                  checked={settings.enableJokerAudience ?? true}
                  onChange={(e) => onUpdateSettings({ enableJokerAudience: e.target.checked })}
                  className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                />
              </label>
              
              <label className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                <span className="text-sm text-gray-700">👑 Best-Player Joker</span>
                <input
                  type="checkbox"
                  checked={settings.enableJokerBestPlayer ?? true}
                  onChange={(e) => onUpdateSettings({ enableJokerBestPlayer: e.target.checked })}
                  className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                />
              </label>
              
              {/* Zeit-Joker removed - admin controls timer via Pause/+20s buttons */}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default GamificationSettings;
